import { DateUtil } from '@dto/util/DateUtil';
import { DatePicker, GetRef } from 'antd';
import type { RangePickerProps } from 'antd/es/date-picker';
import type { Dayjs, OpUnitType } from 'dayjs';
import type { PartialRangeValue, RangeEndpoint, RangePickerConstraints, RangeValue } from './rangePickerState';

/** Pair antd hands to `onCalendarChange`: present, but either slot may still be empty. */
export type AntdPair = Parameters<NonNullable<RangePickerProps['onCalendarChange']>>[0];

export type AntdValue = RangePickerProps['value'];

type PickerRef = GetRef<typeof DatePicker.RangePicker>;

/** antd picker ref with the runtime-supported `focus({ index })` typed. */
export type XmRangeDatePickerRef = Omit<PickerRef, 'focus'> & {
  focus: (options?: FocusOptions & { index?: RangeEndpoint }) => void;
};

export type XmRangeDatePickerProps = RangePickerProps;

export const toPartial = (value: AntdPair | null | undefined): PartialRangeValue | null =>
  (value ? [value[0] ?? null, value[1] ?? null] : null);

export const toPair = (range: PartialRangeValue | null): AntdPair | null => (range ? [range[0], range[1]] : null);

export const toRange = (value: AntdValue | PartialRangeValue): RangeValue | null => (value?.[0] && value[1] ? [value[0], value[1]] : null);

/** Same digits, re-read as the display timezone; a no-op for values that already carry it. */
export const fromDisplayTz = (pair: AntdPair): AntdPair => [DateUtil.fromDisplayTz(pair[0]), DateUtil.fromDisplayTz(pair[1])];

function formatText(format: RangePickerProps['format']): string | undefined {
  const first = Array.isArray(format) ? format[0] : format;
  if (typeof first === 'string') return first;
  if (first && typeof first === 'object') return first.format;
  return undefined;
}

/**
 * Renders one endpoint the way the picker displays it.
 * @param date instant to render
 * @param format antd `format` prop of the current render
 */
export function formatDate(date: Dayjs, format: RangePickerProps['format']): string {
  const first = Array.isArray(format) ? format[0] : format;
  if (typeof first === 'function') return first(date);
  return date.format(formatText(format) ?? DateUtil.DATETIME_FORMAT);
}

/**
 * Precision the model compares ranges at: whatever the format shows.
 * @param props consumer props of the current render
 */
export const precisionOfProps = (props: XmRangeDatePickerProps): OpUnitType =>
  (formatText(props.format)?.includes('ss') ? 'second' : 'minute');

/**
 * Collects everything the reducer validates against out of the antd props.
 * @param props consumer props of the current render
 */
export function constraintsOf(props: XmRangeDatePickerProps): RangePickerConstraints {
  const { disabled, disabledTime } = props;
  return {
    disabledDate: props.disabledDate,
    disabledTime: disabledTime ? (date, role) => disabledTime(date, role, {}) : undefined,
    minDate: props.minDate,
    maxDate: props.maxDate,
    disabledEndpoints: Array.isArray(disabled) ? disabled : [!!disabled, !!disabled],
    allowClear: props.allowClear !== false,
    precision: precisionOfProps(props),
  };
}
